import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress, Divider, Chip,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useBrand } from "../../../../contexts/BrandContext";
import CronEditor from "./CronEditor";
import SelectorBuilder from "./SelectorBuilder";

const CRON_EXAMPLES = [
    { label: "Toutes les heures", cron: "0 * * * *" },
    { label: "Tous les jours à minuit", cron: "0 0 * * *" },
    { label: "Tous les jours à 9h", cron: "0 9 * * *" },
    { label: "Chaque lundi à 3h", cron: "0 3 * * 1" },
    { label: "Toutes les 15 min", cron: "*/15 * * * *" },
];

const COMMON_TIMEZONES = ["UTC", "Europe/Paris", "GMT", "Africa/Douala", "America/New_York"];

const EditInternalJobDialog = ({ open, job, onClose, onSaved }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({
        job_key: "", name: "", description: "", transaction_type: "",
        selector: {}, payload_template: "{}",
        active: true, cron: "", timezone: "UTC",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [transactionTypes, setTransactionTypes] = useState([]);

    // Load event types
    useEffect(() => {
        if (!open || !brandName) return;
        loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types?brand=${encodeURIComponent(brandName)}&origin=INTERNAL&active=true`, {
            headers: { "X-Brand": brandName }
        })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => setTransactionTypes(Array.isArray(data) ? data : []))
            .catch(() => setTransactionTypes([]));
    }, [open, brandName]);

    // Populate form from job
    useEffect(() => {
        if (job) {
            setForm({
                job_key: job.job_key || "",
                name: job.name || "",
                description: job.description || "",
                transaction_type: job.transaction_type || "",
                selector: job.selector && typeof job.selector === "object" ? job.selector : {},
                payload_template: job.payload_template ? JSON.stringify(job.payload_template, null, 2) : "{}",
                active: job.active ?? true,
                cron: job.schedule?.cron || "",
                timezone: job.schedule?.timezone || "UTC",
            });
            setError(null);
        }
    }, [job]);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
    const isValid = form.name.trim() && form.transaction_type.trim();

    const handleSubmit = async () => {
        if (!isValid || !job) return;
        setSubmitting(true);
        setError(null);
        try {
            // selector is already a plain object from SelectorBuilder
            const selector = (form.selector && typeof form.selector === "object") ? form.selector : {};
            let payload_template = null;
            if (form.payload_template.trim() && form.payload_template.trim() !== "{}") {
                try { payload_template = JSON.parse(form.payload_template); }
                catch { throw new Error("Le payload_template n'est pas un JSON valide"); }
            }

            const schedule = form.cron.trim()
                ? { type: "cron", cron: form.cron.trim(), timezone: form.timezone.trim() || "UTC" }
                : null;

            const body = {
                name: form.name.trim() || null,
                description: form.description.trim() || null,
                transaction_type: form.transaction_type.trim(),
                selector,
                payload_template,
                active: form.active,
                schedule,
            };

            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/internal-jobs/${job.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName || job.brand,
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onSaved(); onClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    if (!job) return null;
    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.internalJobs.gradient }}>
                        <WorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            Modifier le Job Interne
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">{job.job_key}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    {/* name */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Nom du job" value={form.name}
                            onChange={handle("name")} size="small"
                            helperText="Nom lisible du job"
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* description */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small"
                            helperText="Description courte (optionnelle)"
                            sx={fieldSx}
                        />
                    </Grid>

                    {/* transaction_type */}
                    <Grid item xs={12}>
                        <TextField fullWidth required select label="Type de transaction (INTERNAL)" value={form.transaction_type}
                            onChange={handle("transaction_type")} size="small" sx={fieldSx}>
                            {/* Also show the current value even if not in list */}
                            {form.transaction_type && !transactionTypes.find(et => et.key === form.transaction_type) && (
                                <MenuItem value={form.transaction_type}><em>{form.transaction_type} (actuel)</em></MenuItem>
                            )}
                            {transactionTypes.map((et) => (
                                <MenuItem key={et.id} value={et.key}>
                                    {et.name} <Typography component="span" fontSize={11} color="text.secondary" ml={1}>({et.key})</Typography>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* selector */}
                    <Grid item xs={12}>
                        <Typography fontSize={12} fontWeight={600} color="text.secondary" mb={1}>
                            Sélecteur de clients
                        </Typography>
                        <SelectorBuilder
                            brandName={brandName}
                            value={form.selector}
                            onChange={(val) => setForm(p => ({ ...p, selector: val }))}
                        />
                    </Grid>


                    {/* Schedule section */}
                    <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }}><Typography fontSize={11} color="text.secondary">Planification du Job</Typography></Divider>
                    </Grid>

                    {/* Cron Editor */}
                    <Grid item xs={12} sm={8}>
                        <CronEditor
                            value={form.cron}
                            onChange={(val) => setForm(p => ({ ...p, cron: val }))}
                        />
                    </Grid>

                    {/* timezone */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            select
                            label="Fuseau horaire"
                            value={form.timezone}
                            onChange={(e) => setForm(p => ({ ...p, timezone: e.target.value }))}
                            size="small"
                            helperText="Ex: UTC, Europe/Paris"
                            sx={fieldSx}
                        >
                            {COMMON_TIMEZONES.map((tz) => (
                                <MenuItem key={tz} value={tz}>
                                    {tz}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>



                    {/* active */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600}>Activer le job</Typography>
                                <Typography fontSize={11} color="text.secondary">Le job sera planifié et exécuté selon le CRON</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.internalJobs.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.internalJobs.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.internalJobs.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditInternalJobDialog;
